import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { CodigoPeca, Sale } from '@/pages/MinhasVendas';
import { formatPhoneBR, formatCPF, validateCPF, getCPFErrorMessage } from '@/lib/garantias-utils';

interface GarantiaModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleForGarantia: Sale | null;
  garantiaFormData: {
    cpf_cliente: string;
    telefone_cliente: string;
    nome_cliente: string;
    codigos: CodigoPeca[];
    idcliente: number | null;
    sale: any;
    iduser: number | null;
    sale_date_created: string;
  };
  foundClient: boolean;
  isSubmittingGarantia: boolean;
  onCpfChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onSearchClient: (dado: string, valor: string) => void;
  onAddCodigo: (codigo: string, tipo: string) => void;
  onRemoveCodigo: (id: number) => void;
  onSubmitGarantia: () => void;
}

export function GarantiaModal({
  isOpen,
  onClose,
  saleForGarantia,
  garantiaFormData,
  foundClient,
  isSubmittingGarantia,
  onCpfChange,
  onPhoneChange,
  onNameChange,
  onSearchClient,
  onAddCodigo,
  onRemoveCodigo,
  onSubmitGarantia
}: GarantiaModalProps) {
  const [cpfError, setCpfError] = React.useState('');

  const handleCpfChange = (value: string) => {
    const formatted = formatCPF(value);
    onCpfChange(formatted);
    
    // Validação em tempo real
    if (formatted) {
      const error = getCPFErrorMessage(formatted);
      setCpfError(error);
    } else {
      setCpfError('');
    }
  };

  const isCpfValid = validateCPF(garantiaFormData.cpf_cliente);
  const isFormValid = garantiaFormData.nome_cliente && 
                     isCpfValid && 
                     garantiaFormData.codigos.length > 0;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col pt-8 px-2">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            Adicionar garantia
            {saleForGarantia?.id ? (
              <span> - Venda <em>#{saleForGarantia.codepagarme || `XDINF${saleForGarantia.id}`}</em></span>
            ) : (
              <span> - <em style={{color: 'rgb(248, 71, 71)'}}>VENDA EXTERNA</em></span>
            )}
          </DialogTitle>
          {!saleForGarantia?.id && (
            <p className="text-xs text-muted-foreground mt-2 leading-tight">
              Atenção: crie garantia de VENDA EXTERNA somente para vendas realizadas fora da plataforma Xizinha.<br/>
              Vendas realizadas por pix, link ou cartão <strong>PELA PLATAFORMA</strong>, a garantia deve ser feita pela página <strong>"Minhas Vendas"</strong>.
            </p>
          )}
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            {/* CPF/CNPJ */}
            <div className="flex gap-2 md:col-span-2 items-start">
              <div className="flex-1">
                <label className="text-sm font-medium">CPF Cliente</label>
                <Input
                  type="text"
                  id="cpf_cliente"
                  placeholder="999.999.999-99"
                  value={garantiaFormData.cpf_cliente}
                  onChange={(e) => handleCpfChange(e.target.value)}
                  maxLength={14}
                  readOnly={foundClient}
                  className={`mt-1 ${cpfError ? 'border-red-500' : ''}`}
                />
                {cpfError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {cpfError}
                  </p>
                )}
              </div>
             
              <Button
                variant="outline"                  
                disabled={!garantiaFormData.cpf_cliente || !isCpfValid}
                className="mt-6 border-gray-400"
                onClick={() => onSearchClient('cpf_cliente', garantiaFormData.cpf_cliente)}
              >
                <Search className="h-4 w-4" /> Buscar
              </Button>
            </div>

            {/* Telefone */}
            <div className="flex gap-2 md:col-span-2">
              <div className="flex-1">
                <label className="text-sm font-medium">Telefone Cliente</label>
                <Input
                  type="text"
                  id="telefone_cliente"
                  placeholder="(99) 99999-9999"
                  value={garantiaFormData.telefone_cliente}
                  onChange={(e) => onPhoneChange(formatPhoneBR(e.target.value))}
                  maxLength={15}
                  readOnly={foundClient}
                  className="mt-1"
                />
              </div>
              {!foundClient && garantiaFormData.telefone_cliente && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-6"
                  onClick={() => onSearchClient('telefone_cliente', garantiaFormData.telefone_cliente)}
                >
                  <Search className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Nome Cliente */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Nome Completo Cliente</label>
              <Input
                type="text"
                placeholder="Nome Completo Cliente"
                value={garantiaFormData.nome_cliente}
                onChange={(e) => onNameChange(e.target.value)}
                readOnly={foundClient}
                className="mt-1"
              />
            </div>

            {/* Data da venda - somente se não há venda associada */}
            {!saleForGarantia?.id && (
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Data da venda</label>
                <Input
                  type="text"
                  value={format(new Date(), "dd/MM/yyyy")}
                  readOnly
                  className="mt-1"
                />
              </div>
            )}

            {/* Códigos das peças */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Códigos das Peças</label>
              <p className='mb-2 text-[0.75rem] text-muted-foreground leading-none'>
                Dica: o código da peça está na etiqueta e possui 13 números. Você pode adicionar mais de um código por certificado, conforme a quantidade de peças compradas pelo(a) cliente.
              </p>
              <CodigosPecasInput
                codigos={garantiaFormData.codigos}
                onAddCodigo={onAddCodigo}
                onRemoveCodigo={onRemoveCodigo}
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0 px-8">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmittingGarantia}
          >
            Cancelar
          </Button>
          <Button
            onClick={onSubmitGarantia}
            disabled={isSubmittingGarantia || !isFormValid}
          >
            {isSubmittingGarantia ? 'Criando...' : 'Gerar garantia'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente para input de códigos de peças
function CodigosPecasInput({ 
  codigos, 
  onAddCodigo, 
  onRemoveCodigo 
}: { 
  codigos: CodigoPeca[]; 
  onAddCodigo: (codigo: string, tipo: string) => void; 
  onRemoveCodigo: (id: number) => void; 
}) {
  const [currentCodigo, setCurrentCodigo] = React.useState('');
  const [selectedTipo, setSelectedTipo] = React.useState('');

  const tiposPeca = [
    'anel',
    'brinco', 
    'corrente',
    'gargantilha',
    'pulseira',
    'tornozeleira',
    'piercing',
    'pingente',
    'conjunto'
  ];

  const handleAddCodigo = () => {
    if (currentCodigo.trim() && selectedTipo) {
      onAddCodigo(currentCodigo.trim(), selectedTipo);
      setCurrentCodigo('');
      setSelectedTipo('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCodigo();
    }
  };

  const handleCodigoChange = (value: string) => {
    // Remove todos os caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    setCurrentCodigo(numericValue);
  };

  return (
    <div className="mt-1">
      <Card className="border-gray-200">
        <div className="p-4 border-b bg-white">
          <h3 className="font-medium text-sm mb-4">Códigos das Peças</h3>
          
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Input
                  type="text"
                  placeholder="Digite o Código"
                  value={currentCodigo}
                  onChange={(e) => handleCodigoChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxLength={13}
                />
              </div>
              
              <div>
                <Select value={selectedTipo} onValueChange={setSelectedTipo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo da peça:" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposPeca.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>
                        {tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="md:col-start-2">
              <Button
                type="button"
                onClick={handleAddCodigo}
                disabled={!currentCodigo.trim() || !selectedTipo}
                className="w-full"
              >
                Adicionar peça
              </Button>
            </div>
          </div>
        </div>
        
        {codigos.length > 0 && (
          <div className="max-h-[300px] overflow-y-auto">
            {codigos.map((codigo) => (
              <div key={codigo.id} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{codigo.code}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    onClick={() => onRemoveCodigo(codigo.id)}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}